import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, HostListener, Inject, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { BehaviorSubject, debounceTime, distinct, distinctUntilChanged, filter, finalize, map, Subject, Subscription } from 'rxjs';
import { filterContainsByValue } from '../is-table/is-table.functions';
import { ISAPI, ISFilterAPI, MVFilter, MVListingConfigs, MVOrdering, MVPagination } from '../models/api';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip'; // type of the tooltip passed to syncTooltip
import { ISMultiSelectDatasource, ISMultiSelectOption, ISMultiSelectStyleObject } from './is-multi-select';
import { HttpClient } from '@angular/common/http';
import { FilterOptions, ISColumn, ISLabelOptions, ISSelectOption } from '../is-table/is-table';
import { DataSource, SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { HttpMethod } from '../is-select/is-select';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { SpinnerService } from '../services/spinner.service';
import { SortDirection } from '../models/generic';

@Component({
    selector: 'is-multi-select',
    templateUrl: './is-multi-select.component.html',
    styleUrls: ['./is-multi-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ISMultiSelectComponent),
            multi: true
        }
    ]
})
export class ISMultiSelectComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

    scrollHandler(event: Event) {
        const dataSource = this.DataSource$?.value;
        if (!dataSource) return;

        const eventTargetElement: any = event.target;
        const menuScrollableHeight = eventTargetElement.scrollHeight - eventTargetElement.offsetHeight;
        const scrolledDistance = eventTargetElement.scrollTop;

        if (!String.isNullOrUndefinedOrWhitespace(dataSource.apiUrl) &&
            this.httpMethod == 'POST' &&
            !this.lastPage &&
            !this.showLoader &&
            !this.showPartialLoader &&
            scrolledDistance >= menuScrollableHeight * 0.9) {
            this.getMoreData(undefined, true);
        }
    }

    @Input() DataSource$: BehaviorSubject<ISMultiSelectDatasource> = new BehaviorSubject<ISMultiSelectDatasource>(new ISMultiSelectDatasource([])); // Contains all the data related to filters, columns, values and request data
    @Input() smallInput: boolean = true; // Determines if the small input is to be used
    @Input() placeholder?: string; // Placeholder string
    @Input() options?: ISMultiSelectOption[]; // Array of options (alternative to sending the dataSource)
    @Input() httpMethod: HttpMethod = "POST"; // Method that is used in the api request
    @Input() emitChangeOnClose: boolean = false; // Determines if the selection changes are emitted after each selection or after closing the modal
    @Input() showMenuFilter: boolean = true; // Determines if the search input is present inside the menu
    @Input() requestOnFocus: boolean = false; // Determines if the API request is made when component is loaded or when it is focused
    @Input() name: string | null = null; // Unique name used to distinguish between multiple multiselect components
    @Input() singleSelectOnly: boolean = false; // Indicates if the multi-select only allows on value selected at a time
    @Input() solidInput: boolean = true; // Indicates if the multi-select will have the form-control-solid class
    @Input() menuHasSameWidthAsInput: boolean = false; // Indicates if the multi-select menu will have the same width as the input
    @Input() parentItemsAsMainSelection: boolean = false; // Indicates if the parent items become the main selection point of the multi-seelct instead of the children
    @Input() selectableParents: boolean = true; // Indicates if the parent items can be selected (when false the parent checkbox is hidden)
    @Input() onlyOneParentSelectionAllowed: boolean = false; // Indicates if multiple children can only be selected as long as they all belong to the same parent (selecting a child of another parent clears the previous selection)
    @Input() prependIcon: boolean = false;
    @Input() avatarField?: string;       // base64 field for single items + children
    @Input() groupAvatarField?: string;  // base64 field for groups (parent)
    @Input() showGroupAvatar: boolean = false;
    @Input() showChildAvatar: boolean = false;
    @Input() showInitials: boolean = false;

    @Input() queryable: boolean = false;

    // Allows the consuming application to show the loader when the data is being requested outside of this component
    @Input() set externalLoading(value: boolean) {
        if (this._externalLoading != value) {
            this._externalLoading = value;
            this.toggleExternalSpinner();
        }
    }
    get externalLoading(): boolean {
        return this._externalLoading;
    }
    private _externalLoading: boolean = false;

    @Output() onDataRetrieved: EventEmitter<any[]> = new EventEmitter<any[]>(); // Emits when the data is retrieved from the API
    @Output() onSelectionChange: EventEmitter<any[]> = new EventEmitter<any[]>(); // Emits when the selected items are changed (also possible to only emit when menu is closed with the emitChangeOnClose variable)

    @ViewChild("Input") input: ElementRef | null = null; // Stores the input element that is being used as the menu trigger
    @ViewChild("FilterInput") filterInput: ElementRef | null = null; // Stores the input element that is being used as the filter
    @ViewChild("menuTrigger") menuTrigger: MatMenuTrigger | null = null; // Stores the menu trigger element

    protected menuWidth: string | null = null;

    protected showLoader: boolean = false; // Used to show the loader
    protected showPartialLoader: boolean = false; //Used to show the loader when lazy loading

    protected selectionModel: SelectionModel<any> = new SelectionModel<MultiSelectObjectForHtml>(true, undefined, true, (label1, label2) => label1.id == label2.id && label1.parentId == label2.parentId);

    onChange = (values: any[]) => { };
    onTouched: any = () => { };

    protected form = new FormGroup({
        search: new FormControl('')
    });

    protected value: any[] = [];
    protected defaultValues: any[] = []; // Used to store the default values on local data
    private _unsubscribe: any[] = []; // Used to store the subscriptions
    private _unsubscribeTranslation: Subscription | null = null;

    protected disabled?: boolean = undefined; // Used to disable the component
    protected requestRequired: boolean = false; // Checks if the component needs to make a request to the API, used when requestOnFocus is set to true
    protected isGrouped: boolean = false; // Indicates that the multiselect will have nested options

    public readonly componentId = crypto.randomUUID(); // Unique Id of this component
    protected itemLabels: MultiSelectObjectForHtml[] = []; // Labels to show in the HTML
    protected dataSourceAux: DataSourceColumnHelper = {};  // Auxiliar object that stores variables that help manage the indexes of the dataSource Data
    protected displayString: BehaviorSubject<string> = new BehaviorSubject<string>('');

    protected formControl: NgControl | null = null;

    protected lastPage: boolean = false;
    protected openAllGroups: boolean = false;
    protected multiSelectKey?: string;

    private _loadedKeys = new Set<string>();

    constructor(
        private _spinnerService: SpinnerService,
        private _httpClient: HttpClient,
        private _dialog: MatDialog,
        private _injector: Injector,
        private _translate: TranslateService,
        private _router: Router
    ) { }

    ngOnInit(): void {
        if (this.singleSelectOnly) {
            this.selectionModel = new SelectionModel<MultiSelectObjectForHtml>(false, undefined, true, (label1, label2) => label1.id == label2.id && label1.parentId == label2.parentId);
        }

        this.DataSource$?.next(this.DataSource$.value);

        this.setOptions();

        // Throws an error if there are no columns on the dataSource
        if (!this.DataSource$?.value.columns || this.DataSource$?.value.columns?.length == 0) {
            throw new Error('Multiselect requires a DataSource');
        }

        this.setDefaultValues(this.DataSource$.value.body, true);

        if (String.isNullOrUndefinedOrWhitespace(this.DataSource$?.value.apiUrl) && (this.DataSource$?.value.body?.length ?? 0) > 0) {
            this.itemLabels = this.getItemsLabelAndData(this.DataSource$.value.body!);
            this.getSelectedString();
        }

        this.setSubscriptions();

    }

    /**
     * If no name was given to the component it uses the name of the formControl bound to it
     */
    ngAfterViewInit() {
        const ngControl: NgControl | null = this._injector.get(NgControl, null);
        if (ngControl) {
            if (this.name == null || this.name == '') {
                this.name = ngControl.name?.toString() ?? null;
            }
            this.formControl = ngControl;
        }

        this.multiSelectKey = `select-${(!String.isNullOrUndefinedOrWhitespace(this.name) ? '' + this.name : '')}${(this._router.url.replace('/', '-'))}`;

        // The external loading may have been set before the spinner key existed
        if (this._externalLoading) {
            this.toggleExternalSpinner();
        }

        if (this.DataSource$.value.apiUrl && (!this.requestOnFocus || this.value.length > 0)) {
            this.getDataFromTheAPI(this.DataSource$.value.apiUrl!, true);
            this.DataSource$.next(this.DataSource$.value);
        } else if (this.DataSource$?.value.apiUrl && this.requestOnFocus) {
            this.requestRequired = true;
        }
    }

    /**
     * Triggers when an input is changed
     * @param changes Object with the input changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes) {
            if (changes['options'] != null && changes['options'].currentValue != changes['options'].previousValue) {
                this.setOptions();
                this.setDefaultValues(this.DataSource$.value.body, true);
            }

            if (changes['DataSource$'] != null && changes['DataSource$'].currentValue != changes['DataSource$'].previousValue) {
                this.setupDatasourceColumns();
            }

        }
    }

    /**
     * Indicates if the main loader is shown, either from an internal request or from the externalLoading input
     */
    protected get isLoading(): boolean {
        return this.showLoader || this._externalLoading;
    }

    /**
     * Shows or hides the spinner according to the externalLoading input
     */
    private toggleExternalSpinner() {
        if (!this.multiSelectKey) {
            return;
        }

        if (this._externalLoading) {
            this._spinnerService.showInComponentSpinner(this.multiSelectKey);
        } else {
            this._spinnerService.hideInComponentSpinner(this.multiSelectKey);
        }
    }

    /**
     * Saves the columns identifiers for ease of access
     */
    protected setupDatasourceColumns() {
        const dataSource = this.DataSource$?.value;
        if (!dataSource) return;

        if (dataSource.parentColumns && dataSource.parentColumns.length > 0) {
            this.dataSourceAux.parentIdentifierId = dataSource.parentColumns[0].identifier;
            this.dataSourceAux.parentDescriptionId = dataSource.parentColumns[1].identifier;
            this.dataSourceAux.parentStyleId = dataSource.parentColumns?.find((column: ISColumn) => column.identifier == 'style')?.identifier ?? undefined;
            this.dataSourceAux.parentIconId = dataSource.parentColumns?.find((column: ISColumn) => column.identifier == 'icon')?.identifier ?? undefined;
            this.dataSourceAux.parentDisableId = dataSource.parentColumns?.find((column: ISColumn) => column.identifier == 'disabled')?.identifier ?? undefined;
            this.dataSourceAux.parentPropertyName = dataSource.parentColumns?.find((column: ISColumn) => column.isParentObject == true)?.identifier ?? undefined;
        }

        if (dataSource.columns && dataSource.columns.length > 0) {
            this.dataSourceAux.childIdentifierId = dataSource.columns[0].identifier;
            this.dataSourceAux.childDescriptionId = dataSource.columns[1].identifier;
            this.dataSourceAux.childStyleId = dataSource.columns.find((column: ISColumn) => column.identifier == 'style')?.identifier ?? undefined;
            this.dataSourceAux.childDisableId = dataSource.columns.find((column: ISColumn) => column.identifier == 'disabled')?.identifier ?? undefined;
            this.dataSourceAux.childIconId = dataSource.columns.find((column: ISColumn) => column.identifier == 'icon')?.identifier ?? undefined;
        }
    }

    /**
     * Handles the selection and deselection of any individual item
     * @param item Item to be selected/deselected
     * @param condition Condition that indicates if the item should be reversed (regular behaviour), only selected or only deselected
     */
    protected toggleSelection(itemLabel: MultiSelectObjectForHtml, childItemLabel?: MultiSelectObjectForHtml, event?: MouseEvent, forceSelection: 'select' | 'deselect' | null = null) {


        // Stops the default click event so that it doesnt close the menu
        if (event) {
            event.preventDefault();
        }

        if (itemLabel.disabled || childItemLabel?.disabled) {
            return;
        }

        // If a child item was sent toggles it without any special behaviour otherwise
        // If a parent item is sent it first checks if it has children or not before selecting
        if (childItemLabel) {
            const toggleableItem = this.selectionModel.selected.find((selectedLabel: MultiSelectObjectForHtml) => this.selectionModel.compareWith!(selectedLabel, childItemLabel));
            if (forceSelection == null) {
                if (toggleableItem) {
                    this.selectionModel.toggle(toggleableItem);
                } else {
                    this.deselectOtherParentsSelections(childItemLabel.parentId);
                    this.selectionModel.select(childItemLabel);
                }
            } else if (forceSelection == 'select') {
                this.deselectOtherParentsSelections(childItemLabel.parentId);
                this.selectionModel.select(childItemLabel);
            } else {
                this.selectionModel.deselect(toggleableItem);
            }

        } else if (itemLabel) {
            // If the parent item has children then it will either select the ones that aren't selected or deselect all of them
            if (itemLabel.children && itemLabel.children.length > 0) {
                if (!this.selectableParents) {
                    return;
                }

                if (this.parentItemsAsMainSelection) {
                    const toggleableItem = this.selectionModel.selected.find((selectedLabel: MultiSelectObjectForHtml) => this.selectionModel.compareWith!(selectedLabel, itemLabel));

                    if (forceSelection == null) {
                        if (toggleableItem) {
                            this.selectionModel.toggle(toggleableItem);
                        } else {
                            this.selectionModel.select(itemLabel);
                        }
                    } else if (forceSelection == 'select') {
                        this.selectionModel.select(itemLabel);
                    } else {
                        this.selectionModel.deselect(toggleableItem);
                    }
                } else {
                    if (forceSelection == null) {
                        let nonSelectedChildren = itemLabel.children.filter((childLabel: MultiSelectObjectForHtml) => !this.selectionModel.isSelected(childLabel));

                        if (nonSelectedChildren.length > 0) {
                            this.deselectOtherParentsSelections(itemLabel.id);
                            this.selectionModel.select(...nonSelectedChildren)
                        } else {
                            let matchingSelections = itemLabel.children.map((childLabel: MultiSelectObjectForHtml) => {
                                return this.selectionModel.selected.find((selectedLabel: MultiSelectObjectForHtml) => this.selectionModel.compareWith!(selectedLabel, childLabel));
                            });
                            this.selectionModel.deselect(...matchingSelections);
                        }
                    } else if (forceSelection == 'select' ) {
                        let nonSelectedChildren = itemLabel.children.filter((childLabel: MultiSelectObjectForHtml) => !this.selectionModel.isSelected(childLabel))

                        if (nonSelectedChildren.length > 0) {
                            this.deselectOtherParentsSelections(itemLabel.id);
                            this.selectionModel.select(...nonSelectedChildren)
                        }
                    } else {
                        let selectedChildren = itemLabel.children.filter((childLabel: MultiSelectObjectForHtml) => this.selectionModel.isSelected(childLabel));

                        if (selectedChildren.length > 0) {
                            let matchingSelections = itemLabel.children.map((childLabel: MultiSelectObjectForHtml) => {
                                return this.selectionModel.selected.find((selectedLabel: MultiSelectObjectForHtml) => this.selectionModel.compareWith!(selectedLabel, childLabel));
                            });
                            this.selectionModel.deselect(...matchingSelections);
                        }
                    }
                }
            } else {
                const toggleableItem = this.selectionModel.selected.find((selectedLabel: MultiSelectObjectForHtml) => this.selectionModel.compareWith!(selectedLabel, itemLabel));
                if (forceSelection == null) {
                    if (toggleableItem) {
                        this.selectionModel.toggle(toggleableItem);
                    } else {
                        this.deselectOtherParentsSelections(itemLabel.parentId);
                        this.selectionModel.select(itemLabel);
                    }

                } else if (forceSelection == 'select') {
                    this.deselectOtherParentsSelections(itemLabel.parentId);
                    this.selectionModel.select(itemLabel);
                } else {
                    this.selectionModel.deselect(toggleableItem);
                }
            }
        }
    }

    /**
     * When onlyOneParentSelectionAllowed is active, deselects every selected item that doesn't belong to the given parent
     * @param parentId Id of the parent whose children are about to be selected (undefined for items without a parent)
     */
    protected deselectOtherParentsSelections(parentId?: string) {
        if (!this.onlyOneParentSelectionAllowed || this.parentItemsAsMainSelection) {
            return;
        }

        const otherParentsSelections = this.selectionModel.selected.filter((selectedLabel: MultiSelectObjectForHtml) => selectedLabel.parentId != parentId);

        if (otherParentsSelections.length > 0) {
            this.selectionModel.deselect(...otherParentsSelections);
        }
    }

    /**
     * Called when the value of the filter is changed
     * @param newVal New value of the filter
     * @param localFiltering Indicates if the filtering is local or on the API
     */
    protected filterValueChange(newVal: string | null, localFiltering: boolean) {
        const query = newVal ?? '';

        if (localFiltering) {
            // restaura dataset base
            this.DataSource$.value.body = this.defaultValues;
            this.DataSource$.value.pagination.page = 1;

            // Filters the data locally
            this.DataSource$.value.body = filterContainsByValue<any>(this.DataSource$.value.body, this.form.controls.search.value ?? '', this.parentItemsAsMainSelection ? this.dataSourceAux.parentDescriptionId! : this.dataSourceAux.childDescriptionId!);
            const base = this.defaultValues ?? [];

            // grouped local filtering: match parent OR any child
            if (!String.isNullOrUndefinedOrWhitespace(this.dataSourceAux.parentPropertyName)) {
                const parentDescField = this.dataSourceAux.parentDescriptionId!;
                const childDescField = this.dataSourceAux.childDescriptionId!;
                const childrenProp = this.dataSourceAux.parentPropertyName!;

                const filtered = base
                    .map((p: any) => {
                        const parentMatches = this.includesNormalized(p?.[parentDescField], query);

                        const children = (p?.[childrenProp] ?? []) as any[];
                        const matchingChildren = children.filter((c: any) => this.includesNormalized(c?.[childDescField], query));

                        if (parentMatches) {
                            // se o parent bate, mantém todos os children
                            return p;
                        }

                        if (matchingChildren.length > 0) {
                            // se só bate nos filhos, mantém só os filhos que batem (sem mutar o original)
                            return { ...p, [childrenProp]: matchingChildren };
                        }

                        return null;
                    })
                    .filter(Boolean);

                this.DataSource$.value.body = filtered as any[];
                this.itemLabels = this.getItemsLabelAndData(this.DataSource$.value.body!);
                this.DataSource$.next(this.DataSource$.value);
                return;
            }

            // non-grouped local filtering (case + acentos)
            const descField = this.dataSourceAux.childDescriptionId!;
            this.DataSource$.value.body = base.filter((x: any) => this.includesNormalized(x?.[descField], query));
            this.itemLabels = this.getItemsLabelAndData(this.DataSource$.value.body!);
            this.DataSource$.next(this.DataSource$.value);
            return;
        }

        // API: mantém lógica, mas mete também filtro no parent (se existir)
        this.DataSource$.value.columns[1].filterOptions.value = query;

        if (this.DataSource$.value.parentColumns && this.DataSource$.value.parentColumns.length > 1) {
            this.DataSource$.value.parentColumns[1].filterOptions.value = query;
        }

        this.getDataFromTheAPI(this.DataSource$.value.apiUrl!, true, false);
        this.DataSource$.next(this.DataSource$.value);
    }


    /**
     * @description Set the subscriptions in the search input and the selected values
     */
    protected setSubscriptions() {
        // search subscription
        let searchSub: Subscription;

        if (!String.isNullOrUndefinedOrWhitespace(this.DataSource$.value.apiUrl) && this.httpMethod == 'POST') {
            searchSub = this.form.controls.search.valueChanges
                .pipe(debounceTime(500), distinctUntilChanged())
                .subscribe((newVal) => {
                    this.openAllGroups = !String.isNullOrUndefinedOrWhitespace(newVal);
                    this.filterValueChange(newVal, false);
                });
        } else {
            searchSub = this.form.controls.search.valueChanges
                .pipe(distinctUntilChanged())
                .subscribe((newVal) => {
                    this.openAllGroups = !String.isNullOrUndefinedOrWhitespace(newVal);
                    this.filterValueChange(newVal, true);
                });
        }

        this._unsubscribe.push(searchSub);

        // selection subscription
        const selSub = this.selectionModel.changed.subscribe((modelChanges: SelectionChange<MultiSelectObjectForHtml>) => {
            this.getSelectedString();
            this.value = this.selectionModel.selected.map(item => item.id);

            this.performSelectionChecks(modelChanges);

            if (!this.emitChangeOnClose) {
                this.onSelectionChange.emit(this.selectionModel.selected);
                this.onChange(this.value);
            }
        });

        this._unsubscribe.push(selSub);
    }


    /**
     * When the selected values are changed it takes the newly selected and/or the deselected values and changes their check value
     * @param modelChanges Variable that represents the latest change to the selectionModel
     */
    protected performSelectionChecks(modelChanges: SelectionChange<MultiSelectObjectForHtml>) {
        let parentsToCheck: MultiSelectObjectForHtml[] = [];

        // Newly selected values get checked
        modelChanges.added.forEach((addedItem: MultiSelectObjectForHtml) => {
            if (addedItem.parentId) {
                const parentItem = this.itemLabels.find((itemLabel: MultiSelectObjectForHtml) => itemLabel.id == addedItem.parentId);

                if (parentItem) {
                    parentsToCheck.push(parentItem);
                    const childItem = parentItem.children?.find((childLabel: MultiSelectObjectForHtml) => childLabel.id == addedItem.id);

                    if (childItem) {
                        childItem.checked = true;
                    }
                }

            } else {
                const childItem = this.itemLabels?.find((childLabel: MultiSelectObjectForHtml) => childLabel.id == addedItem.id);

                if (childItem) {
                    childItem.checked = true;
                }
            }
        });

        // Deselected values get unchecked
        modelChanges.removed.forEach((removedItem: MultiSelectObjectForHtml) => {
            if (removedItem.parentId) {
                const parentItem = this.itemLabels.find((itemLabel: MultiSelectObjectForHtml) => itemLabel.id == removedItem.parentId);

                if (parentItem) {
                    parentsToCheck.push(parentItem);
                    const childItem = parentItem.children?.find((childLabel: MultiSelectObjectForHtml) => childLabel.id == removedItem.id);

                    if (childItem) {
                        childItem.checked = false;
                    }
                }
            } else {
                const childItem = this.itemLabels?.find((childLabel: MultiSelectObjectForHtml) => childLabel.id == removedItem.id);

                if (childItem) {
                    childItem.checked = false;
                }
            }
        });

        // Checks the parents of the changed selections and checks if they are checked or indeterminate
        if (parentsToCheck.length > 0) {
            // Removes duplicate parents from the array
            let uniqueParentIds = parentsToCheck.reduce<MultiSelectObjectForHtml[]>((unique: MultiSelectObjectForHtml[], parent: MultiSelectObjectForHtml) => unique.findIndex((un) => un.id == parent.id) > -1 ? unique : [...unique, parent], []);

            uniqueParentIds.forEach((parent: MultiSelectObjectForHtml) => {
                // Is checked when every child is selected
                parent.checked = parent.children?.every((childLabel: MultiSelectObjectForHtml) => childLabel.checked);
                // Is indeterminate when it isn't checked but it has at least one child selected
                parent.indeterminate = !parent.checked && parent.children?.some((childLabel: MultiSelectObjectForHtml) => childLabel.checked);
            });
        }
    }

    /**
     * @description Get more data from the API and add to the array
     */
    protected getMoreData(url?: string, fromScrollEvent: boolean = false) {
        let apiUrl = this.DataSource$.value.apiUrl ?? url;
        if (!String.isNullOrUndefinedOrWhitespace(apiUrl)) {
            if (fromScrollEvent) {
                this.DataSource$.value.pagination.page++; // <-- fica aqui
            }

            this.getDataFromTheAPI(apiUrl, false, false, fromScrollEvent);
            this.DataSource$.next(this.DataSource$.value);
        }
    }


    /**
     * Grabs the data from the API
     * @param APIUrl Url the request is pointed at
     * @param resetData Indicates if the data is appended or replaces the already present values
     * @param refreshSelectionData Indicates if the when the request is complete the selected elements will be reset
     * @param scrollEvent Indicates if this function is called on scrolling
     */
    protected getDataFromTheAPI(APIUrl?: string, resetData: boolean = true, refreshSelectionData: boolean = true, scrollEvent: boolean = false) {
        let requestUrl: string | undefined = APIUrl ?? this.DataSource$.value.apiUrl;
        let table: ISAPI | MVListingConfigs;
        const pagination = this.DataSource$.value.pagination;

        if (scrollEvent) {
            this.showPartialLoader = true;
        } else {
            this.showLoader = true;
        }

        this._spinnerService.showInComponentSpinner(this.multiSelectKey!);

        // Clears the previous DataSource body (to represent a fully new result set)
        if (resetData) {
            this.DataSource$.value.body = [];
            this.defaultValues = [];
            this.lastPage = false;
            this.DataSource$.value.pagination.page = 1;
            this._loadedKeys.clear();
        }

        if (!String.isNullOrUndefinedOrWhitespace(requestUrl)) {
            // POST
            if (this.httpMethod == "POST") {
                // Build filter object based on queryable flag
                if (this.queryable) {
                    // NEW: MVListingConfigs
                    const mvFilters: MVFilter[] = [];

                    this.DataSource$.value.columns.forEach((column: ISColumn) => {
                        let filterVal = column.filterOptions?.value;

                        if (filterVal !== null && filterVal !== undefined && filterVal !== '') {
                            mvFilters.push(new MVFilter(
                                column.filterOptions.searchField ?? column.identifier,
                                filterVal,
                                column.filterOptions?.type || 'text',
                                column.filterOptions?.operator || 'contains',
                                column.filterOptions?.navigation
                            ));
                        }
                    });

                    this.DataSource$.value.parentColumns?.forEach((column: ISColumn) => {
                        const filterVal = column.filterOptions?.value;
                        if (filterVal !== null && filterVal !== undefined && filterVal !== '') {
                            mvFilters.push(new MVFilter(
                                column.filterOptions.searchField ?? column.identifier,
                                filterVal,
                                column.filterOptions?.type || 'text',
                                column.filterOptions?.operator || 'contains',
                                column.filterOptions?.navigation
                            ));
                        }
                    });

                    this.DataSource$.value.filters?.forEach((column: ISFilterAPI) => {
                        const filterVal = column?.value;
                        if (filterVal !== null && filterVal !== undefined && filterVal !== '') {
                            mvFilters.push(new MVFilter(
                                column.searchField ?? column.identifier,
                                filterVal,
                                column?.type || 'text',
                                column?.operator || 'contains',
                                column?.navigation
                            ));
                        }
                    });

                    const orderingColumn = this.DataSource$.value.columns.find(c => c.direction && (c.direction == SortDirection.asc || c.direction == SortDirection.desc));
                    const ordering = orderingColumn
                        ? new MVOrdering(
                            orderingColumn.filterOptions.searchField ?? orderingColumn.identifier,
                            orderingColumn.direction,
                            orderingColumn.filterOptions?.navigation
                        )
                        : null;

                    table = new MVListingConfigs(
                        mvFilters,
                        ordering,
                        new MVPagination(pagination.page, pagination.pageSize)
                    );

                } else {
                    table = new ISAPI([], pagination);

                    this.DataSource$.value.columns.forEach((column: ISColumn) => {
                        (table as ISAPI).filters.push({
                            identifier: column.identifier,
                            value: column.filterOptions?.value,
                            type: column.filterOptions?.type,
                            operator: column.filterOptions?.operator,
                            orderType: column.direction,
                            searchField: column.filterOptions?.searchField,
                            navigation: column.filterOptions?.navigation
                        });
                    });

                    this.DataSource$.value.parentColumns?.forEach((column: ISColumn) => {
                        (table as ISAPI).filters.push({
                            identifier: column.identifier,
                            value: column.filterOptions?.value,
                            type: column.filterOptions?.type,
                            operator: column.filterOptions?.operator,
                            orderType: column.direction,
                            searchField: column.filterOptions?.searchField,
                            navigation: column.filterOptions?.navigation
                        });
                    });

                    this.DataSource$.value.filters?.forEach(filter => {
                        (table as ISAPI).filters.push(filter);
                    });
                }

                this._httpClient.post(requestUrl!, table)
                    .pipe(finalize(() => this._spinnerService.hideInComponentSpinner(this.multiSelectKey!)))
                    .subscribe((res: any) => {

                        const query = this.form.controls.search.value ?? '';
                        const hasTotal = res?.TotalListCount != null && res?.TotalListCount > 0;

                        // FE fallback filter (para quando BE não filtra)
                        let pageData: any[] = this.filterApiData(res?.data ?? [], query);

                        // dedupe por key
                        const uniquePageData: any[] = [];
                        for (const item of pageData) {
                            const key = this.getDedupeKeyFromApiItem(item);
                            if (!this._loadedKeys.has(key)) {
                                this._loadedKeys.add(key);
                                uniquePageData.push(item);
                            }
                        }

                        // push data deduped
                        this.DataSource$.value.body?.push(...uniquePageData);

                        // total (se existir)
                        if (hasTotal) {
                            this.DataSource$.value.totalItems = res.TotalListCount;
                        }

                        if (scrollEvent) {
                            this.showPartialLoader = false;
                            if (uniquePageData.length > 0) {
                                this.itemLabels.push(...this.getItemsLabelAndData(uniquePageData));
                            } else {
                                // se não veio nada novo, acabou (evita loop infinito)
                                this.lastPage = true;
                            }
                        } else {
                            this.showLoader = false;
                            this.itemLabels = this.getItemsLabelAndData(uniquePageData);
                        }

                        // ✅ heurística de fim quando não há total:
                        // - se a página devolvida (já filtrada) é menor que pageSize, é muito provável que seja a última
                        if (!hasTotal && (pageData.length < this.DataSource$.value.pagination.pageSize)) {
                            this.lastPage = true;
                        }

                        // ✅ se tem total, também dá para inferir fim
                        if (hasTotal && (this.DataSource$.value.body?.length ?? 0) >= this.DataSource$.value.totalItems) {
                            this.lastPage = true;
                        }

                        // re-aplica seleções quando necessário
                        if (this.selectionModel.selected.length == 0 && this.value.length > 0) {
                            this.select(undefined, this.value, undefined, refreshSelectionData);
                        }

                        // Emite sempre os dados originais do BE (ou podes trocar para uniquePageData se preferires)
                        this.onDataRetrieved.emit(res?.data ?? []);
                    });

            }
            // GET
            else {
                this._httpClient.get(requestUrl!)
                    .pipe(finalize(() => this._spinnerService.hideInComponentSpinner(this.multiSelectKey!)))
                    .subscribe((res: any) => {

                        const query = this.form.controls.search.value ?? '';
                        const hasTotal = res?.TotalListCount != null && res?.TotalListCount > 0;

                        // FE fallback filter (para quando BE não filtra)
                        let pageData: any[] = this.filterApiData(res?.data ?? [], query);

                        // dedupe (evita repetir para sempre)
                        const uniquePageData: any[] = [];
                        for (const item of pageData) {
                            const key = this.getDedupeKeyFromApiItem(item);
                            if (!this._loadedKeys.has(key)) {
                                this._loadedKeys.add(key);
                                uniquePageData.push(item);
                            }
                        }

                        // mantém defaultValues apenas se fizer sentido (local filtering normalmente não se aplica a API,
                        // mas deixo para não mudar comportamento existente)
                        this.defaultValues.push(...uniquePageData);

                        this.DataSource$.value.body?.push(...uniquePageData);

                        // total (se existir)
                        if (hasTotal) {
                            this.DataSource$.value.totalItems = res.TotalListCount;
                        }

                        if (scrollEvent) {
                            this.showPartialLoader = false;

                            if (uniquePageData.length > 0) {
                                this.itemLabels.push(...this.getItemsLabelAndData(uniquePageData));
                            } else {
                                // não veio nada novo -> acabou (evita loop infinito)
                                this.lastPage = true;
                            }

                        } else {
                            this.showLoader = false;

                            // Em reset, queremos o conjunto atual (deduped)
                            this.itemLabels = this.getItemsLabelAndData(uniquePageData);
                        }

                        // heurística de fim quando não há total:
                        if (!hasTotal && (pageData.length < this.DataSource$.value.pagination.pageSize)) {
                            this.lastPage = true;
                        }

                        // se tem total, para quando atingimos o total
                        if (hasTotal && (this.DataSource$.value.body?.length ?? 0) >= this.DataSource$.value.totalItems) {
                            this.lastPage = true;
                        }

                        // re-aplica seleções quando necessário
                        if (this.selectionModel.selected.length == 0 && this.value.length > 0) {
                            this.select(undefined, this.value, undefined, refreshSelectionData);
                        }

                        // Emite dados (mantive os dados originais do BE para não alterar contratos)
                        this.onDataRetrieved.emit(res?.data ?? []);
                    });
            }

        } else {
            console.error("No URL was provided to the MultiSelect");
        }
    }

    /**
     * Sets the default value which is used in local filtering
     * @param data Data to save as default
     * @param reset Indicates wether the data is appended or if it replaces the previous default values
     */
    protected setDefaultValues(data?: any[], reset: boolean = false) {
        if (reset) {
            this.defaultValues = [];
        }
        if (data && data.length > 0) {
            this.defaultValues.push(...data);
        }
    }

    /**
     * Takes the provided options and saves them to the datasource with the created columns
     */
    protected setOptions() {
        if (this.options) {
            //create the columns by the option keys
            let columns: ISColumn[] = [];
            let parentColumns: ISColumn[] = [];

            columns.push(new ISColumn('value', '', new FilterOptions('')));
            columns.push(new ISColumn('label', '', new FilterOptions('')));
            columns.push(new ISColumn('icon', '', new FilterOptions('')));
            columns.push(new ISColumn('disabled', '', new FilterOptions('')));
            columns.push(new ISColumn('style', '', new FilterOptions('')));

            if (this.options.some((option: ISMultiSelectOption) => option.children != null && option.children.length > 0)) {
                parentColumns.push(new ISColumn('value', '', new FilterOptions('')));
                parentColumns.push(new ISColumn('label', '', new FilterOptions('')));
                parentColumns.push(new ISColumn('icon', '', new FilterOptions('')));
                parentColumns.push(new ISColumn('style', '', new FilterOptions('')));
                parentColumns.push(new ISColumn('disabled', '', new FilterOptions('')));
                parentColumns.push(new ISColumn('children', '', new FilterOptions(''), undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, true));
            }

            this.DataSource$ = new BehaviorSubject<ISMultiSelectDatasource>(new ISMultiSelectDatasource(columns, parentColumns, this.options));
            this.DataSource$.next(this.DataSource$.value);

            this.setupDatasourceColumns();
            this.itemLabels = this.getItemsLabelAndData(this.options);
        } else {
            this.setupDatasourceColumns();
        }


    }

    /**
     * Focuses on the search input
     */
    protected inputFocus(clickEvent?: MouseEvent) {
        if (this.filterInput) {
            this.filterInput.nativeElement.focus();
        }

        const inputElement: HTMLElement | null = document.getElementById('input_' + this.componentId);

        if (inputElement) {
            const menuElement: HTMLElement | null = document.getElementById('menu_' + this.componentId);

            /* if (menuElement) {
                 if (!this.menuHasSameWidthAsInput) {
                     if (inputElement.offsetWidth > 250) {
                         menuElement.parentElement!.parentElement!.style.maxWidth = inputElement.offsetWidth + 'px';
                         menuElement.style.width = inputElement.offsetWidth + 'px';
                     } else {
                         //menuElement.parentElement!.parentElement!.style.maxWidth = '250px';
                         //menuElement.style.width = '250px';
                     }
                 } else {
                     menuElement.parentElement!.parentElement!.style.maxWidth = inputElement.offsetWidth + 'px';
                     menuElement.style.width = inputElement.offsetWidth + 'px';
                 }

             }*/
        }

        if (this.requestOnFocus && this.requestRequired) {
            this.getDataFromTheAPI(this.DataSource$.value.apiUrl!, true);
            this.DataSource$.next(this.DataSource$.value);
            this.requestRequired = false;
        }
    }

    /**
     * Runs when the selection menu is closed
     */
    protected onMenuClose() {
        // Emits the selected values
        if (this.emitChangeOnClose) {
            this.onSelectionChange.emit(this.selectionModel.selected);
            this.onChange(this.value);
        }

        // Blurs the html so that the textbox is unfocused and it doesn't show whats beyond the elipsis in case of overflow
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }

    /**
     * Function that runs when the multiselect is open
    */
    protected onMenuOpen() {
        let openLabels = this.itemLabels.filter((item: MultiSelectObjectForHtml) => item.open == true);
        let openLabelsIds = openLabels.map((label: MultiSelectObjectForHtml) => label.id);

        openLabels.forEach((item: MultiSelectObjectForHtml) => item.open = false);

        // takes care of resizing and so needs to happen ASAP while still waiting for the DOM to load hence the lower timeout
        setTimeout(() => {
            this.inputFocus();
        }, 50);

        // takes care of remarking certain groups as open, since the MatExpansionPanel takes longer to load and doesn't work with just setting expanded to true the solution implemented here "closes" the panels and then "opens" them once they are loaded
        setTimeout(() => {
            this.markPanelsAsOpen(openLabelsIds);
        }, 200);
    }

    /**
     * Takes a data object and converts it to labels based on the columns in the DataSource
     * @param dataToConvert Data object to be converted
     * @returns Array of labels
     */
    protected getItemsLabelAndData(dataToConvert: any[]): MultiSelectObjectForHtml[] {
        let returnObject: MultiSelectObjectForHtml[] = [];
        if (dataToConvert != null && dataToConvert.length > 0) {
            // If there is a column with the name of the Array property then treats the data as grouped data
            if (!String.isNullOrUndefinedOrWhitespace(this.dataSourceAux.parentPropertyName)) {
                returnObject = dataToConvert.map((item: any) => {
                    let id = item[this.dataSourceAux.parentIdentifierId!].toString();
                    let description = item[this.dataSourceAux.parentDescriptionId!].toString();

                    let returnItem = new MultiSelectObjectForHtml(id, description);
                    returnItem.raw = item;

                    if (this.dataSourceAux.parentStyleId) {
                        returnItem.style = item[this.dataSourceAux.parentStyleId];
                    }

                    if (this.dataSourceAux.parentIconId) {
                        returnItem.icon = item[this.dataSourceAux.parentIconId];
                    }

                    if (this.dataSourceAux.parentDisableId) {
                        returnItem.disabled = item[this.dataSourceAux.parentDisableId];
                    }


                    let itemChildren = item[this.dataSourceAux.parentPropertyName!];

                    // If the item has any children it converts them as well
                    if (itemChildren.length > 0) {

                        returnItem.open = this.openAllGroups;
                        returnItem.children = this.getItemChildren(item, id);

                        returnItem.checked = returnItem.children?.every((childLabel: MultiSelectObjectForHtml) => childLabel.checked);
                        returnItem.indeterminate = !returnItem.checked && returnItem.children?.some((childLabel: MultiSelectObjectForHtml) => childLabel.checked);
                    }

                    return returnItem;
                });
            } else {
                returnObject = dataToConvert.map((item: any) => {
                    let id = item[this.dataSourceAux.childIdentifierId!].toString();
                    let description = item[this.dataSourceAux.childDescriptionId!].toString();

                    let returnItem = new MultiSelectObjectForHtml(id, description);
                    returnItem.raw = item;

                    if (this.dataSourceAux.childStyleId) {
                        returnItem.style = item[this.dataSourceAux.childStyleId];
                    }

                    if (this.dataSourceAux.childIconId) {
                        returnItem.icon = item[this.dataSourceAux.childIconId];
                    }

                    if (this.dataSourceAux.childDisableId) {
                        returnItem.disabled = item[this.dataSourceAux.childDisableId];
                    }

                    if (this.selectionModel.selected.length > 0) {
                        let itemIsAlreadySelected: boolean = this.selectionModel.selected.findIndex((selectedItem: MultiSelectObjectForHtml) => selectedItem.id == id && selectedItem.parentId == null) > -1;
                        returnItem.checked = itemIsAlreadySelected;
                    }

                    return returnItem;
                });
            }
        } else {

            returnObject = [];
        }

        if (returnObject.length > 0) {
            setTimeout(() => this.inputFocus(), 50);
        }

        return returnObject;
    }

    /**
     * Converts a child group into labels
     * @param item Original item
     * @param parentId Id of the parent label
     * @returns
     */
    protected getItemChildren(item: any, parentId: string): MultiSelectObjectForHtml[] {
        let returnObject: MultiSelectObjectForHtml[] = [];

        returnObject = item[this.dataSourceAux.parentPropertyName!].map((childItem: any) => {
            let id = childItem[this.dataSourceAux.childIdentifierId!].toString();
            let description = childItem[this.dataSourceAux.childDescriptionId!].toString();

            let returnObjectChild = new MultiSelectObjectForHtml(id, description);
            returnObjectChild.raw = childItem;
            returnObjectChild.parentId = parentId;

            if (this.dataSourceAux.childStyleId) {
                returnObjectChild.style = ' ' + childItem[this.dataSourceAux.childStyleId] + ' ';
            }

            // If there are any style conditions runs their functions for each of the children
            if (this.DataSource$.value.styleConditions && this.DataSource$.value.styleConditions.length > 0) {
                let style = this.DataSource$.value.styleConditions.find((styleCondition: ISMultiSelectStyleObject) => {
                    return styleCondition.conditionFunction.call(this, childItem);
                })?.style;

                if (!returnObjectChild.style) {
                    returnObjectChild.style = '';
                }

                if (style) {
                    returnObjectChild.style += ' ' + style + ' ';
                }
            }

            if (this.dataSourceAux.childIconId) {
                returnObjectChild.icon = childItem[this.dataSourceAux.childIconId];
            }

            if (this.dataSourceAux.childDisableId) {
                returnObjectChild.disabled = childItem[this.dataSourceAux.childDisableId];
            }

            if (this.selectionModel.selected.length > 0) {
                let itemIsAlreadySelected: boolean = this.selectionModel.selected.findIndex((selectedItem: MultiSelectObjectForHtml) => selectedItem.id == id && selectedItem.parentId == parentId) > -1;

                returnObjectChild.checked = itemIsAlreadySelected;
            }

            return returnObjectChild;
        });


        return returnObject;
    }


    protected getSelectedItem(itemLabel: MultiSelectObjectForHtml): MultiSelectObjectForHtml {
        return this.selectionModel.selected.find((selectedLabel: MultiSelectObjectForHtml) => selectedLabel.id == itemLabel.id && selectedLabel.parentId == itemLabel.parentId);
    }

    /**
     * Sorts the data so that selected items stay at the top
     * Orders by selected and then by ascending alphabetical order
     */
    protected get sortedDataSource() {
        return [...this.DataSource$.value.body!].sort((next: any, curr: any) => this.selectionModel.isSelected(curr) === this.selectionModel.isSelected(next) ? 0 : this.selectionModel.isSelected(next) ? - 1 : this.DataSource$.value.body!.indexOf(next) - this.DataSource$.value.body!.indexOf(curr));
    }

    protected keypressHandler(event: KeyboardEvent) {
        if (event.key == 'Enter') {
            this.menuTrigger?.closeMenu();
            this.input?.nativeElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
        }
    }

    protected markPanelsAsOpen(ids: string[]) {
        this.itemLabels.forEach((itemLabel: MultiSelectObjectForHtml) => {
            if (ids.includes(itemLabel.id)) {
                if ((itemLabel.children?.length ?? 0 > 0)) {
                    itemLabel.open = true;
                }
            }
        });
    }

    //#region Public overrides
    /**
     * Public override that returns all the selected objects (Original object from the API not the label)
     */
    public get selectedValues() {
        const body = this.DataSource$.value.body ?? [];
        const selected = this.selectionModel.selected;

        // grouped
        if (!String.isNullOrUndefinedOrWhitespace(this.dataSourceAux.parentPropertyName)) {
            const childrenProp = this.dataSourceAux.parentPropertyName!;
            const childIdField = this.dataSourceAux.childIdentifierId!;

            // flatten children da body
            const allChildren = body.flatMap((p: any) => (p?.[childrenProp] ?? []));
            return allChildren.filter((child: any) =>
                selected.some((s: MultiSelectObjectForHtml) => s.id == child?.[childIdField]?.toString())
            );
        }

        // non-grouped
        const idField = this.dataSourceAux.childIdentifierId!;
        return body.filter((item: any) =>
            selected.some((s: MultiSelectObjectForHtml) => s.id == item?.[idField]?.toString() && !s.parentId)
        );
    }


    /**
     * Public override for the GetDataFromTheAPI
     */
    public refreshDataFromAPI() {
        this.getDataFromTheAPI(undefined, true, true);
    }

    /**
     * When a call to the API can't be made with filters and has an individual URL per filter this allows to keep appending the resulting data to the multiSelect
     */
    public appendDataFromAPI(url: string) {
        this.getMoreData(url, false);
    }

    /**
     * @description Clear the selected items
     */
    public clearSelected() {
        this.selectionModel.clear();
        this.onChange([]);
    }

    /**
     * @description Check if the item is selected
     * @param  {any} item item to check
     * @returns boolean - true if the item is selected
     */
    public isSelected(itemId?: string, childItemId?: string): boolean {
        if (!String.isNullOrUndefinedOrWhitespace(childItemId)) {
            let childItem = this.selectionModel.selected.find((selectedItem: any) => selectedItem[this.dataSourceAux.childIdentifierId!] == childItemId);
            return childItem != null;
        } else {
            if (!String.isNullOrUndefinedOrWhitespace(itemId)) {
                let item = this.itemLabels.find((itemLabel: MultiSelectObjectForHtml) => itemLabel.id == itemId);
                if (item) {
                    if (item.children && item.children.length > 0) {
                        return item.children.every((childLabel: MultiSelectObjectForHtml) => this.selectionModel.isSelected(childLabel));
                    } else {
                        return this.selectionModel.isSelected(item);
                    }
                }
            }
        }

        return false;
    }

    /**
     * @description Returns all selected records
     */
    public get selectedRecords() {
        return this.selectionModel.selected;
    }

    /**
     * Selects multiple items based on either their index or their Id
     * @param indexes Array of indexes of the items to be selected
     * @param ids Array of ids of the items to select
     * @param resetSelected Indicates if the list of already selected values is to be reset or to be appended to
     */
    public select(indexes?: number[], ids?: any[], childIds?: any[], refreshSelectionData: boolean = false) {
        let itemsToSelect: any[] = [];

        if (refreshSelectionData) {
            this.selectionModel.clear();
        }

        if (indexes && indexes.length > 0) {
            indexes.forEach((index: number) => {
                let possibleIndex: boolean = this.DataSource$.value.body != null && this.DataSource$.value.body.length > index;

                if (possibleIndex) {
                    let itemId = this.DataSource$.value.body?.at(index)[this.dataSourceAux.parentIdentifierId!];

                    let itemLabel = this.itemLabels.find((label: MultiSelectObjectForHtml) => itemId == label.id);

                    if (itemLabel) {
                        this.toggleSelection(itemLabel, undefined, undefined, 'select');
                    }
                }
            });
        }
        if (childIds && childIds.length > 0) {
            let flattenedLabels: MultiSelectObjectForHtml[] = this.itemLabels.reduce<MultiSelectObjectForHtml[]>((accumulator: MultiSelectObjectForHtml[], label: MultiSelectObjectForHtml) => {
                accumulator.push(...(label.children ?? [label]));
                return accumulator;
            }, [] as MultiSelectObjectForHtml[]);

            childIds.forEach((id: string) => {
                let itemLabel = flattenedLabels?.find((item) => item.id == id);
                if (itemLabel != null) {
                    this.toggleSelection(itemLabel, undefined, undefined, 'select');
                }
            });
        }
        if (ids && ids.length > 0) {

            let flattenedLabels: MultiSelectObjectForHtml[] = this.itemLabels.reduce<MultiSelectObjectForHtml[]>((accumulator: MultiSelectObjectForHtml[], label: MultiSelectObjectForHtml) => {
                accumulator.push(...(label.children ?? [label]));
                return accumulator;
            }, [] as MultiSelectObjectForHtml[]);

            ids.forEach((id: string) => {
                let itemLabel = flattenedLabels?.find((item) => item.id == id);
                if (itemLabel != null) {
                    this.toggleSelection(itemLabel, undefined, undefined, 'select');
                }
            });
        }
    }

    /**
     * Deselects multiple items based on either their index or their Id
     * @param indexes Array of indexes of the items to be deselected
     * @param ids Array of ids of the items to deselect
     */
    public deselect(indexes?: number[], ids?: any[]) {
        let itemsToDeselect: any[] = [];
        if (indexes && indexes.length > 0) {
            indexes.forEach((index: number) => itemsToDeselect.push(this.DataSource$.value.body?.at(index)));
        }
        if (ids && ids.length > 0) {
            ids.forEach((id: string) => itemsToDeselect.push(this.DataSource$.value.body?.find((item) => item[this.DataSource$.value.columns[0].identifier] == id)));
        }

        if (itemsToDeselect && itemsToDeselect.length > 0) {
            let itemsToSelect = this.selectionModel.selected.filter((sel: any) => !itemsToDeselect.includes(sel));
            this.selectionModel.setSelection(...itemsToSelect);
        }

    }

    private normalizeText(val: any): string {
        return (val ?? '')
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();
    }

    private includesNormalized(text: any, query: string): boolean {
        if (String.isNullOrUndefinedOrWhitespace(query)) return true;
        return this.normalizeText(text).includes(this.normalizeText(query));
    }

    /** key usada para dedupe (grouped -> parentId, non-grouped -> itemId) */
    private getDedupeKeyFromApiItem(item: any): string {
        // grouped: a API devolve "parents"
        if (!String.isNullOrUndefinedOrWhitespace(this.dataSourceAux.parentPropertyName)) {
            return `P:${item?.[this.dataSourceAux.parentIdentifierId!]?.toString()}`;
        }

        // non-grouped
        return `I:${item?.[this.dataSourceAux.childIdentifierId!]?.toString()}`;
    }

    /** fallback de filtro no FE quando BE não filtra (parent OR child) */
    private filterApiData(data: any[], query: string): any[] {
        if (String.isNullOrUndefinedOrWhitespace(query)) return data;

        // grouped
        if (!String.isNullOrUndefinedOrWhitespace(this.dataSourceAux.parentPropertyName)) {
            const parentDesc = this.dataSourceAux.parentDescriptionId!;
            const childDesc = this.dataSourceAux.childDescriptionId!;
            const childrenProp = this.dataSourceAux.parentPropertyName!;

            return (data ?? [])
                .map((p: any) => {
                    const parentMatches = this.includesNormalized(p?.[parentDesc], query);
                    const children = (p?.[childrenProp] ?? []) as any[];
                    const matchingChildren = children.filter((c: any) => this.includesNormalized(c?.[childDesc], query));

                    if (parentMatches) return p;
                    if (matchingChildren.length > 0) return { ...p, [childrenProp]: matchingChildren };
                    return null;
                })
                .filter(Boolean) as any[];
        }

        // non-grouped
        const desc = this.dataSourceAux.childDescriptionId!;
        return (data ?? []).filter((x: any) => this.includesNormalized(x?.[desc], query));
    }

    /**
     * Enables the tooltip only when the text is cut off. Done on mouseenter instead of in a template
     * binding: isTruncated reads the layout, which is not ready on the first change detection pass,
     * so the binding changed value after being checked (NG0100).
     */
    public syncTooltip(tooltip: MatTooltip, el: HTMLElement | null): void {
        tooltip.disabled = !this.isTruncated(el);
    }

    public isTruncated(el: HTMLElement | null): boolean {
        if (!el) return false;
        return el.scrollWidth > el.clientWidth;
    }

    public initials(text: string): string {
        const t = (text ?? '').trim();
        if (!t) return '?';
        const parts = t.split(/\s+/).filter(Boolean);
        const a = parts[0]?.[0] ?? '';
        const b = parts.length > 1 ? (parts[1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
        return (a + b).toUpperCase();
    }

    /**
         * Takes the selected values and returns a string from all their descriptions
         * @returns All descriptions in a single string
         */
    protected getSelectedString() {
        const selected = this.selectionModel.selected ?? [];
        if (this._unsubscribeTranslation) {
            this._unsubscribeTranslation.unsubscribe();
            this._unsubscribeTranslation = null;
        }

        if (selected.length === 0) {
            this.displayString.next('');
            return;
        }

        const keys = selected.map(s => s.description);

        this._unsubscribeTranslation = this._translate.stream(keys).subscribe((obj: any) => {
            const values = Object.values(obj) as string[];
            const first = values[0] ?? '';
            const extra = values.length - 1;

            if (extra <= 0) {
                this.displayString.next(first);
            } else {
                this.displayString.next(`${first}, +${extra}`);
            }
        });
    }

    public getAvatarSrcFromRaw(raw: any, field?: string): string | null {
        if (!raw || String.isNullOrUndefinedOrWhitespace(field)) return null;

        const b64 = raw?.[field!];
        if (!b64) return null;

        const str = b64.toString().trim();
        if (!str) return null;

        // já vem como data url?
        if (str.startsWith('data:image/')) return str;

        // sempre png base64
        return `data:image/png;base64,${str}`;
    }

    public get inputId() {
        return 'input_' + this.componentId;
    }
    //#endregion

    //#region ControlValueAccessor
    /**
     * @description Allows Angular to update the model.
     * @param  {any[]} values - The value to update
     * @returns void
     */
    writeValue(values: any[]): void {
        if (values == null) {
            values = [];
        }

        this.value = values;

        this.select(undefined, values, undefined, true);

    }

    /**
     * @description Allows Angular to register a function to call when the model changes.
     * @param  {(values:any[])=>void} fn
     * @returns void
     */
    registerOnChange(fn: (values: any[]) => void): void {
        this.onChange = fn;
    }

    /**
     * @description Allows Angular to register a function to call when the input has been touched.
     * @param  {()=>void} fn
     * @returns void
     */
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * @description Allows Angular to disable the input.
     * @param  {boolean} isDisabled
     * @returns void
     */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        isDisabled ? this.form.controls.search.disable({ emitEvent: false }) : this.form.controls.search.enable({ emitEvent: false });
    }
    //#endregion

    /**
     * @description On Destroy component destroy the subscriptions
     */
    ngOnDestroy(): void {
        this._unsubscribe.forEach(sub => sub.unsubscribe());
    }
}

/**
 * Object that facilitate the usage of the data from the API
 */
class MultiSelectObjectForHtml {
    public raw?: any;

    constructor(
        id: string,
        description: string
    );
    constructor(
        id: string,
        description: string,
        children?: MultiSelectObjectForHtml[],
        parentId?: string,
    );
    constructor(
        id: string,
        description: string,
        children?: MultiSelectObjectForHtml[],
        parentId?: string,
        style?: string,
        icon?: string
    );
    /**
     * @param id Id of the original object
     * @param description Description of the original object
     * @param children Children labels
     * @param parentId Id of the parent on the original object
     * @param style Styling of the label
     * @param icon Icon to prepend to the label
     * @param checked Indicates if the label is checked
     * @param indeterminate Indicates if the label is indeterminate (only for the parent)
     * @param open Indicates if the expansion panel is open
     * @param disabled Indicates if the item is intended to be a pickable object or just visual
     */
    constructor(
        public id: string,
        public description: string,
        public children?: MultiSelectObjectForHtml[],
        public parentId?: string,
        public style?: string,
        public icon?: string,
        public checked?: boolean,
        public indeterminate?: boolean,
        public open?: boolean,
        public disabled?: boolean
    ) {

    }
}

/**
 * Object that keeps the IDs of the columns of the DataSource for ease of access
 */
class DataSourceColumnHelper {
    /**
     * @param parentIdentifierId Id of the column that has the parent's Id
     * @param parentDescriptionId Id of the column that has the parent's description
     * @param parentStyleId Id of the column that has the parent's style
     * @param parentIconId Id of the column that has the parent's icon
     * @param parentDisableId If the parent can be selected
     * @param parentPropertyName Name of the property that is the array of children
     * @param childIdentifierId Id of the column that has the child's Id
     * @param childDescriptionId Id of the column that has the child's description
     * @param childStyleId Id of the column that has the child's style
     * @param childIconId Id of the column that has the child's icon
     * @param childDisableId If the child can be selected
     */
    constructor(
        public parentIdentifierId?: string,
        public parentDescriptionId?: string,
        public parentStyleId?: string,
        public parentIconId?: string,
        public parentDisableId?: string,

        public parentPropertyName?: string,
        public childIdentifierId?: string,
        public childDescriptionId?: string,
        public childStyleId?: string,
        public childIconId?: string,
        public childDisableId?: string
    ) {

    }
}
